<template>
  <div class="profile-container">
    <el-card class="profile-card">
      <div slot="header">
        <span>个人信息</span>
      </div>
      <div v-if="profileLoading" class="loading-container">
        <el-skeleton :rows="5" animated />
      </div>
      <el-form v-else :model="profileForm" label-width="120px" ref="profileForm" :rules="rules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="单位名称" prop="companyName">
              <el-input v-model="profileForm.companyName" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="客户编号" prop="customerCode">
              <el-input v-model="profileForm.customerCode" disabled></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactPerson">
              <el-input v-model="profileForm.contactPerson" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系电话" prop="phone">
              <el-input v-model="profileForm.phone" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="地址" prop="address">
              <el-input v-model="profileForm.address" :disabled="!isEditing"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item v-if="!isEditing">
          <el-button type="primary" @click="startEditing">编辑</el-button>
        </el-form-item>
        
        <el-form-item v-else>
          <el-button type="primary" @click="submitForm" :loading="loading">保存</el-button>
          <el-button @click="cancelEditing">取消</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'CustomerProfile',
  data() {
    return {
      profileLoading: false,
      loading: false,
      isEditing: false,
      originalProfile: {}, // 用于保存原始数据
      profileForm: {
        id: '',
        companyName: '',
        customerCode: '',
        contactPerson: '',
        phone: '',
        address: ''
      },
      rules: {
        companyName: [
          { required: true, message: '单位名称不能为空', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        contactPerson: [
          { required: true, message: '联系人不能为空', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '联系电话不能为空', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '地址不能为空', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadProfileData()
  },
  methods: {
    loadProfileData() {
      try {
        // 调用API获取客户信息
        this.profileLoading = true
        adminApi.getCustomerInfo()
          .then(res => {
            if (res) {
              this.profileForm = {
                id: res.id || '',
                companyName: res.companyName || '',
                customerCode: res.customerCode || '',
                contactPerson: res.contactPerson || '',
                phone: res.phone || '',
                address: res.address || ''
              }
              
              // 更新本地存储的用户信息，以便在其他地方使用
              const userInfoStr = localStorage.getItem('userInfo')
              if (userInfoStr) {
                const userInfo = JSON.parse(userInfoStr)
                const updatedUserInfo = {
                  ...userInfo,
                  id: res.id,
                  companyName: res.companyName,
                  customerCode: res.customerCode,
                  contactPerson: res.contactPerson,
                  phone: res.phone,
                  address: res.address,
                  userName: res.companyName // 更新显示的用户名
                }
                localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo))
              }
              
              // 保存原始数据用于取消编辑时恢复
              this.originalProfile = {...this.profileForm}
            }
          })
          .catch(error => {
            this.$message.error('获取客户信息失败: ' + (error.message || '未知错误'))
            
            // 获取失败时尝试从本地存储获取信息作为备选
            this.loadFromLocalStorage()
          })
          .finally(() => {
            this.profileLoading = false
          })
      } catch (error) {
        console.error('加载个人信息失败:', error)
        this.$message.error('加载个人信息失败: ' + error.message)
        
        // 出错时从本地存储获取信息
        this.loadFromLocalStorage()
      }
    },
    
    loadFromLocalStorage() {
      try {
        const userInfoStr = localStorage.getItem('userInfo')
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr)
          if (userInfo) {
            this.profileForm = {
              id: userInfo.id || '',
              companyName: userInfo.companyName || userInfo.userName || '',
              customerCode: userInfo.customerCode || '',
              contactPerson: userInfo.contactPerson || '',
              phone: userInfo.phone || '',
              address: userInfo.address || ''
            }
            this.originalProfile = {...this.profileForm}
          }
        }
      } catch (error) {
        console.error('从本地存储加载个人信息失败:', error)
      }
    },
    
    startEditing() {
      this.isEditing = true
    },
    
    cancelEditing() {
      this.profileForm = {...this.originalProfile}
      this.isEditing = false
    },
    
    submitForm() {
      this.$refs.profileForm.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            // 调用API修改个人信息
            await adminApi.updateCustomer(this.profileForm)
            
            // 更新本地存储的用户信息
            const userInfoStr = localStorage.getItem('userInfo')
            if (userInfoStr) {
              const userInfo = JSON.parse(userInfoStr)
              const updatedUserInfo = {
                ...userInfo,
                companyName: this.profileForm.companyName,
                customerCode: this.profileForm.customerCode,
                contactPerson: this.profileForm.contactPerson,
                phone: this.profileForm.phone,
                address: this.profileForm.address,
                userName: this.profileForm.companyName // 更新显示的用户名
              }
              localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo))
            }
            
            this.$message.success('个人信息更新成功')
            this.isEditing = false
            this.originalProfile = {...this.profileForm}
          } catch (error) {
            this.$message.error('更新个人信息失败: ' + (error.message || '未知错误'))
          } finally {
            this.loading = false
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped>
.profile-container {
  padding: 20px;
}

.profile-card {
  max-width: 900px;
  margin: 0 auto;
}

.el-form-item {
  margin-bottom: 22px;
}

.loading-container {
  padding: 20px 0;
}
</style> 